<template>
    <div>
        <!-- journey lines -->
        <here-polyline v-if="!showRawData" :points="journey.dataLines" with-arrows></here-polyline>

        <!-- raw data journey -->
        <div v-if="showRawData">
            <here-polyline :points="journey.rawData" with-arrows color="#eb7375"></here-polyline>
        </div>

        <!-- speeding -->
        <div v-if="showSpeedingData">
            <div v-for="(speedBlock, index) in journey.speeding" :key="`speeding-block-${index}`">
                <here-polyline :points="speedBlock.dataLines" with-arrows color="#eb7375"></here-polyline>

                <here-info-bubble name="speeding-bubble" :key="index" :lat="speedBlock.points[0].lat" :lng="speedBlock.points[0].lng" :component="speedingInfoBubble" :data="{
                    speedPoint: speedBlock
                }">
                    <here-marker
                        :lat="speedBlock.points[0].lat"
                        :lng="speedBlock.points[0].lng"
                        :icon="{
                                    bitmap: iconsSpeeding
                                }"
                        :map="map"
                        :z-index="5"
                    ></here-marker>
                </here-info-bubble>
                <here-circle
                    :key="`speeding-circle-${index}`"
                    :lat="speedBlock.points[0].lat"
                    :lng="speedBlock.points[0].lng"
                    :radius="50"
                    stroke-color="rgb(235, 115, 117)"
                    fill-color="rgba(235, 115, 117, 0.7)"
                ></here-circle>
            </div>
        </div>

        <!-- events -->
        <div v-if="isClusteringActive">
            <here-info-bubble
                v-if="clusterEventData && clusterEventData.data"
                name="event-cluster-bubble"
                :key="clusterEventData.data.id"
                :lat="clusterEventData.lat"
                :lng="clusterEventData.lng"
                :component="isMultiEvents ? multiEventInfoBubble : eventInfoBubble"
                isClusteringBubble
                :onClearClusterBubbleData="onClearClusterBubbleData"
                :data="{
                    event: clusterEventData.data,
                    eventTypes: getEventTypes,
                }"
            />

                <here-clustering-layer
                    :options="{
                        clusteringOptions: {
                            eps: 12,
                            minWeight: 2
                        }
                    }"
                    :theme="eventsCustomTheme"
                    :onClusterBubbleOpen="onClusterBubbleOpen"
                    :onClearClusterBubbleData="onClearClusterBubbleData"
                    :data="journey.events.map(e => ({
                            ...e.locations[0],
                            data: {
                                ...e,
                                ...e.locations[0],
                            }
                        }))"

                ></here-clustering-layer>
        </div>

        <div v-if="!isClusteringActive">
            <here-info-bubble v-for="event in journey.events.filter(item => item.locations[0])" name="event-bubble" :key="event.id" :lat="event.locations[0].lat" :lng="event.locations[0].lng" :component="eventInfoBubble" :data="{
                    event: event,
                    eventTypes: getEventTypes
                }">
                <here-marker
                    v-if="event.isVisible"
                    :key="event.id"
                    :lat="event.locations[0].lat"
                    :lng="event.locations[0].lng"
                    :icon="{
                            bitmap: iconsEvent
                        }"
                    :map="map"
                ></here-marker>
            </here-info-bubble>
        </div>

        <!-- speed limits -->
        <div v-if="iconComponent">
            <here-info-bubble v-for="limit of journeyRawOrRegularLimits" name="test-bubble" :key="limit.id" :lat="limit.latitude" :lng="limit.longitude" :component="speedInfoBubble" :data="{
                speed: limit.speed,
                heading: limit.heading,
                hasRequestVideoPermissions: hasAllRoles(['VideoRequests']) && checkMediaAccessibilityLicense(),
                date: limit.date,
                hardwareId: limit.hardwareId,
                latitude: limit.latitude,
                longitude: limit.longitude,
                registrationNumber: limit.registrationNumber || 'Unknown',
                driverName: limit.driverName || 'Unknown',
                status: limit.status,
                isHardwareOnline: limit.isHardwareOnline,
                store: $store,
                updateVideoRequest: updateVideoRequest
            }">
                <here-marker
                    :lat="limit.latitude"
                    :lng="limit.longitude"
                    :icon-component="iconComponent"
                >
                </here-marker>
            </here-info-bubble>
        </div>


        <!-- idle points -->
        <div v-if="showIdlePoints">
            <here-info-bubble v-for="(idle, index) in journey.idles" name="idle-bubble" :key="index" :lat="idle.lat" :lng="idle.lng" :component="idleInfoBubble" :data="{
                idlePoint: idle
            }">
                <div>
                    <here-marker
                        :key="index"
                        :lat="idle.lat"
                        :lng="idle.lng"
                        :icon="{
                                bitmap: iconsIdle
                            }"
                        :map="map"
                        :z-index="5"
                    ></here-marker>
                    <here-circle
                        :key="`idle-circle-${index}`"
                        :lat="idle.lat"
                        :lng="idle.lng"
                        :radius="50"
                        stroke-color="rgb(253, 255, 0)"
                        fill-color="rgba(253, 255, 0, 0.7)"
                    ></here-circle>
                </div>
            </here-info-bubble>
        </div>

        <!-- start point -->
        <here-marker
            v-if="journey.startPoint"
            :key="`start-point-${journey.id}`"
            :lat="journey.startPoint.lat"
            :lng="journey.startPoint.lng"
            :icon="{
                    bitmap: iconsStart,
                    options: { anchor: { x: 15, y: 42 } }
                }"
            :map="map"
        ></here-marker>

        <!-- end point -->
        <here-marker
            v-if="journey.endPoint"
            :key="`end-point-${journey.id}`"
            :lat="journey.endPoint.lat"
            :lng="journey.endPoint.lng"
            :icon="{
                    bitmap: iconsEnd,
                    options: { anchor: { x: 15, y: 42 } }
                }"
            :map="map"
        ></here-marker>
    </div>
</template>

<script>
import HereMarker from '@/components/maps/here/components/HereMarker'
import HereClusteringLayer from '@/components/maps/here/components/HereClusteringLayer'
import HerePolyline from '@/components/maps/here/components/HerePolyline'
import HereInfoBubble from '@/components/maps/here/components/HereInfoBubble'
import { mapGetters } from 'vuex'
import SpeedInfoBubble from '@/components/atoms/SpeedInfoBubble'
import EventInfoBubble from '@/components/atoms/EventInfoBubble'
import MultiEventInfoBubble from '@/components/atoms/MultiEventInfoBubble'
import HereCircle from '@/components/maps/here/components/HereCircle'
import IdleInfoBubble from '@/components/atoms/IdleInfoBubble'
import SpeedingInfoBubble from '@/components/atoms/SpeedingInfoBubble'
import {show} from "assjs/src/internal/show";

export default {
    name: 'HereJourney',
    inject: ['map', 'ui', 'group'],
    data () {
        return {
            iconsStart: '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><g transform="translate(1 1)"><g transform="translate(-619 -1270)"><path d="M13.458,39.193C2.107,22.737,0,21.048,0,15a15,15,0,1,1,30,0c0,6.048-2.107,7.737-13.458,24.193a1.876,1.876,0,0,1-3.083,0Z" transform="translate(619 1270)" fill="#db1238" stroke="#db1238" stroke-width="2"/></g><circle cx="13" cy="13" r="13" transform="translate(2 2)" fill="#fff"/><circle cx="9" cy="9" r="9" transform="translate(6 6)" fill="#05d72f"/></g></svg>',
            iconsEnd: '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><g transform="translate(1 1)"><g transform="translate(-619 -1270)"><path d="M13.458,39.193C2.107,22.737,0,21.048,0,15a15,15,0,1,1,30,0c0,6.048-2.107,7.737-13.458,24.193a1.876,1.876,0,0,1-3.083,0Z" transform="translate(619 1270)" fill="#db1238" stroke="#db1238" stroke-width="2"/></g><circle cx="13" cy="13" r="13" transform="translate(2 2)" fill="#fff"/><g transform="translate(6.56 6.539)"><g transform="translate(1.8)"><path d="M16.56.866H3.4V.289A.306.306,0,0,0,3.084,0H2.121A.306.306,0,0,0,1.8.289V17.612a.306.306,0,0,0,.321.289h.963a.306.306,0,0,0,.321-.289V10.683H16.56a.306.306,0,0,0,.321-.289V1.155A.306.306,0,0,0,16.56.866ZM2.763,17.323H2.442V.577h.321ZM16.239,4.331H13.031V7.218h3.209v2.887H13.031V7.218H9.822v2.887H6.613V7.218H3.4V4.331H6.613V1.444H9.822V4.331h3.209V1.444h3.209Z" transform="translate(-1.8)" fill="#db1238"/><rect width="2.4" height="2.4" transform="translate(5.141 4.471)" fill="#db1238"/></g></g></g></svg>',
            iconsEvent: '<svg xmlns="http://www.w3.org/2000/svg" width="31.809" height="40.521" viewBox="0 0 31.809 40.521"><path d="M26.871,4.267a15.764,15.764,0,0,0-11.79-4.2A15.94,15.94,0,0,0,0,15.965c0,11.47,11.2,21.341,14.578,24.129a2.122,2.122,0,0,0,1.325.457,2.034,2.034,0,0,0,1.325-.457A53.023,53.023,0,0,0,25.637,31.5c4.2-5.438,6.261-10.785,6.169-15.812A15.585,15.585,0,0,0,26.871,4.267Z" transform="translate(0 -0.03)" fill="#fff"/><path d="M18.664,41.074C16.151,39.063,5,29.375,5,18.818A13.605,13.605,0,0,1,17.887,5.2h.823A13.557,13.557,0,0,1,32.328,18.636C32.511,29.192,21.177,39.017,18.664,41.074Z" transform="translate(-2.715 -2.838)" fill="#fe2e58"/><g transform="translate(6.079 12.761)"><g transform="translate(0 0)"><path d="M16.75,6.593l-2.872.287a.569.569,0,0,0-.479.574.562.562,0,0,0,.526.479h.048l2.872-.287a.569.569,0,0,0,.479-.574A.5.5,0,0,0,16.75,6.593Z" transform="translate(-6.986 -3.432)" fill="#fff"/><path d="M18.954,3.779l-3.685-.957a.978.978,0,0,1-.431-.239L12.54.62A2.7,2.7,0,0,0,11.1.045L7.514,0A4.3,4.3,0,0,0,3.877,1.768l-.622.862-1.532.191A2.062,2.062,0,0,0,0,4.879V6.411A1.473,1.473,0,0,0,1.532,7.895h.383A1.613,1.613,0,0,0,2.489,8.9c.1.1.239.191.335.287.048.048.144.1.191.144a2.076,2.076,0,0,0,1.1.239,2.256,2.256,0,0,0,2.2-1.675h7.371A1.613,1.613,0,0,0,14.263,8.9c.1.1.239.191.335.287.048.048.144.1.191.144a2.1,2.1,0,0,0,1.1.287,2.256,2.256,0,0,0,2.2-1.675c1.579-.048,2.345-.574,2.345-1.532v-.67A1.959,1.959,0,0,0,18.954,3.779ZM4.116,8.565A1.292,1.292,0,1,1,5.409,7.273,1.39,1.39,0,0,1,4.116,8.565Zm11.774,0a1.292,1.292,0,1,1,1.292-1.292A1.318,1.318,0,0,1,15.89,8.565Zm3.542-2.2c0,.383-.67.479-1.244.526a2.317,2.317,0,0,0-2.3-1.915,2.1,2.1,0,0,0-1.1.287.661.661,0,0,0-.191.144,2.026,2.026,0,0,0-.335.239,1.947,1.947,0,0,0-.622,1.244H6.366a2.411,2.411,0,0,0-2.3-1.915,2.672,2.672,0,0,0-1.1.287c-.048,0-.1.048-.144.1a2.026,2.026,0,0,0-.335.239,1.947,1.947,0,0,0-.622,1.244H1.532a.492.492,0,0,1-.526-.479V4.832a.986.986,0,0,1,.862-1.005L3.4,3.587a1.033,1.033,0,0,0,.67-.431l.574-.766A3.344,3.344,0,0,1,7.467,1l3.59.1a1.668,1.668,0,0,1,.814.287l2.25,1.962a2.43,2.43,0,0,0,.862.431l3.685.957a1.021,1.021,0,0,1,.766,1.005Z" transform="translate(0 0.005)" fill="#fff"/></g><path d="M1.54,4.62l1.771-.814,1.2-.479.862-.67,1.2-.766L8.528,1.7h2.824a3.981,3.981,0,0,1,1.388.191c.239.191.766.766.766.766l.814.814a6.083,6.083,0,0,0,1.053.622c.239.1,1.292.335,1.292.335l1.484.383,1.1.383,1.1.909s.144.862.144,1.149-.1.383-.574.574a5.572,5.572,0,0,1-.814.287L18.1,8.449l-.526.862-.766.574a4.4,4.4,0,0,1-.814,0,10.749,10.749,0,0,1-1.34-.718,1.5,1.5,0,0,0-.574-.622A6.883,6.883,0,0,0,12.548,8.4H10.107a10.12,10.12,0,0,0-1.723-.1c-.287.1-1.34.048-1.532.1a1.489,1.489,0,0,0-.526.048C6.278,8.5,5.7,9.6,5.7,9.6a2.579,2.579,0,0,1-.814.383c-.239,0-.718.1-.957-.1s-.67-.622-.67-.622A6.392,6.392,0,0,0,2.928,8.4a6.279,6.279,0,0,1-.718-.144c-.144-.048-.862-.239-.909-.383-.048-.1-.383-.526-.239-.718S1.061,5.1,1.54,4.62Z" transform="translate(-0.535 -0.884)" fill="#fff"/></g></svg>',
            invisibleIcon: '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" opacity="0.00"/>',
            iconsIdle: '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><g transform="translate(-618 -1269)"><path d="M13.458,39.193C2.107,22.737,0,21.048,0,15a15,15,0,0,1,30,0c0,6.048-2.107,7.737-13.458,24.193a1.876,1.876,0,0,1-3.083,0Z" transform="translate(619 1270)" fill="yellow" stroke="#000" stroke-width="2"/></g></svg>',
            iconsSpeeding: '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42"><g transform="translate(-618 -1269)"><path d="M13.458,39.193C2.107,22.737,0,21.048,0,15a15,15,0,0,1,30,0c0,6.048-2.107,7.737-13.458,24.193a1.876,1.876,0,0,1-3.083,0Z" transform="translate(619 1270)" fill="#eb7375" stroke="#000" stroke-width="2"/></g></svg>',
            hitArea: null,
            iconComponent: null,
            iconEventComponent: null,
            speedInfoBubble: SpeedInfoBubble,
            eventInfoBubble: EventInfoBubble,
            multiEventInfoBubble: MultiEventInfoBubble,
            idleInfoBubble: IdleInfoBubble,
            speedingInfoBubble: SpeedingInfoBubble,
            updateVideoRequest: true,
            eventsCustomTheme: {},
            clusterSvgTemplate: null,
            clusterEventData: {
                data: null,
                lat: null,
                lng: null
            },
            isMultiEvents: false
        }
    },
    created () {
        this.iconComponent = new H.map.Icon(this.invisibleIcon)
    },
    beforeMount() {
        this.initEventClusterTheme()
    },
    mounted () {
        this.hitArea = new H.map.HitArea(H.map.HitArea.ShapeType.CIRCLE, [1, 1, 1])
    },
    beforeDestroy () {
        this.removeExistingBubbles()
    },
    components: {
        HereCircle,
        HereMarker,
        HerePolyline,
        HereInfoBubble,
        HereClusteringLayer
    },
    computed: {
        ...mapGetters({
            getEventTypes: 'dictionaryModule/getEventTypes',
            showIdlePoints: 'trackingModule/showIdlePoints',
            showRawData: 'trackingModule/showRawData',
            showSpeedingData: 'trackingModule/showSpeedingData',
            isClusteringActive: 'trackingModule/getIsClusteringActive'
        }),
        journeyRawOrRegularLimits () {
            return this.showRawData ? this.journey.rawLimits : this.journey.limits
        }
    },
    watch: {
        'showRawData': {
            handler (newVal, oldVal) {
                this.removeExistingBubbles()
            }
        }
    },
    props: {
        journey: {
            type: Object,
            required: true
        }
    },
    methods: {
        show,
        removeExistingBubbles () {
            const existingBubbles = this.ui().getBubbles()
            existingBubbles.forEach(bubble => this.ui().removeBubble(bubble))
        },
        onClearClusterBubbleData () {
            this.clusterEventData.data = null
        },
        onClusterBubbleOpen (eventData, clusterLocation) {
            if (Array.isArray(eventData)) {
                this.isMultiEvents = true
                this.clusterEventData.lat = clusterLocation.lat
                this.clusterEventData.lng = clusterLocation.lng
            } else {
                this.isMultiEvents = false
                this.clusterEventData.lat = eventData.lat
                this.clusterEventData.lng = eventData.lng
            }

            this.clusterEventData.data = eventData
        },
        initEventClusterTheme () {
            this.iconEventComponent = new H.map.Icon(this.iconsEvent, {
                size: { w: 32, h: 40 }
            })

            this.clusterSvgTemplate = (number) => {
                return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="74.328" height="74.328" viewBox="0 0 74.328 74.328">
                            <defs>
                                <filter id="a" x="0" y="0" width="74.328" height="74.328" filterUnits="userSpaceOnUse">
                                    <feOffset dy="-2" input="SourceAlpha"/><feGaussianBlur stdDeviation="3" result="b"/>
                                    <feFlood flood-opacity="0.161"/>
                                    <feComposite operator="in" in2="b"/>
                                    <feComposite in="SourceGraphic"/>
                                </filter>
                            </defs>
                            <g transform="translate(-1221.414 -598.713)">
                                <g transform="translate(1230.414 609.713)">
                                    <g transform="translate(0 0)">
                                        <g transform="translate(0 0)">
                                            <g transform="translate(0 28.164) rotate(-45)">
                                                <g transform="matrix(0.71, 0.71, -0.71, 0.71, 21.33, -34.06)" filter="url(#a)">
                                                    <path d="M20.364,0c11,0,19.466,8.916,19.466,19.915,0,7.556,0,19.915,0,19.915H19.915A20.036,20.036,0,0,1,4.526,32.556,19.621,19.621,0,0,1,0,19.915C0,8.916,9.365,0,20.364,0Z" transform="translate(37.16 11) rotate(45)" fill="#fff"/>
                                                </g>
                                                <path d="M17.7,0A17.7,17.7,0,1,1,4.022,28.935,17.437,17.437,0,0,1,0,17.7,17.7,17.7,0,0,1,17.7,0Z" transform="translate(2.215 2.216)" fill="#adadad"/>
                                            </g>
                                        </g>
                                    </g>
                                    <text transform="translate(21.164 32.664)" fill="#fff" font-size="18" text-anchor="middle" font-family="OpenSans-Bold, Open Sans" font-weight="700">
                                        <tspan x="8" y="0">${number}</tspan>
                                    </text>
                                </g>
                            </g>
                        </svg>`
            }

            this.eventsCustomTheme = {
                getClusterPresentation: (cluster) => {
                    let weight = cluster.getWeight()

                    if (6 <= weight) weight = 3.5
                    if (4 <= weight && 6 > weight) weight = 3

                    const radius = weight * 8
                    const diameter = radius * 2.5
                    const number = cluster.getWeight()
                    const svgString = this.clusterSvgTemplate(number).replace(/\{radius\}/g, radius).replace(/\{diameter\}/g, diameter).replace(/\{text\}/g, number)

                    const clusterIcon = new H.map.Icon(svgString, {
                            size: { w: diameter, h: diameter },
                            anchor: { x: diameter / 2, y: diameter / 2 + radius }
                        }),

                        clusterMarker = new H.map.Marker(cluster.getPosition(), {
                            icon: clusterIcon,
                            min: cluster.getMinZoom(),
                            max: cluster.getMaxZoom()
                        })

                    clusterMarker.setData(cluster)
                    return clusterMarker
                },
                getNoisePresentation: (noisePoint) => {
                    const noiseMarker = new H.map.Marker(noisePoint.getPosition(), {
                        icon: this.iconEventComponent,
                        min: noisePoint.getMinZoom()
                    })

                    noiseMarker.setData(noisePoint)
                    return noiseMarker
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.cluster {
    position: relative;
}

.cluster-number-position {
    color: green !important;
}
</style>
